import React, { useState } from "react";

// React Router Dom
import { Link, useNavigate, useSearchParams } from "react-router-dom";

// Hooks
import useSnackbarNotification from "../hooks/useSnackbarNotification";

// React Icons
import { FaEye, FaEyeSlash } from "react-icons/fa";

// Contexts
import { useAuth } from "../contexts/AuthContext";

const LoginPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectParam = searchParams.get('redirect');

    const { signIn } = useAuth();
    const { showError, showSuccess } = useSnackbarNotification();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const { error } = await signIn(email, password);

            if (error) throw error;
            
            // Check for redirect parameter in URL or localStorage
            const storedRedirect = localStorage.getItem('redirectAfterLogin');
            const shouldRedirectToMockInterview = redirectParam === 'mock-interview' || storedRedirect === 'mock-interview';
            
            // Clear the stored redirect after using it
            if (storedRedirect) {
                localStorage.removeItem('redirectAfterLogin');
            }
            
            // Redirect to mock interview page if redirect parameter is set
            const redirectPath = shouldRedirectToMockInterview
                ? '/practice-interview-questions' 
                : '/';
            navigate(redirectPath);
            showSuccess("Login successful");
            setLoading(false);
        } catch (error) {
            console.error("Login error:", error);
            showError("Login failed");
            setError(
                error.message ||
                    "Failed to log in. Please check your email and password."
            );
        }
        setLoading(false);
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="min-h-screen flex">
            <div className="hidden md:flex md:w-1/2 bg-gray-50  flex-col space-y-6 overflow-auto">
                <img
                    src="/login.jpg"
                    alt="Login"
                    className="w-full h-full object-cover"
                />
            </div>

            <div className="w-full md:w-1/2 bg-gray-50 flex flex-col justify-center px-8 md:px-20">
                <div className="max-w-md w-full mx-auto">
                    <div
                        className="logo cursor-pointer justify-center mb-8"
                        onClick={() => navigate("/")}
                    >
                        Doctors' Jobs <span className="logo-badge">Beta</span>
                    </div>

                    <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                        Welcome Back!
                    </h2>

                    {error && (
                        <div className="mb-6 rounded-md bg-red-50 p-4 text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                            >
                                Email Address *
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="password"
                                className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                            >
                                Password *
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={togglePasswordVisibility}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                >
                                    {showPassword ? (
                                        <FaEyeSlash className="h-5 w-5 text-gray-400" />
                                    ) : (
                                        <FaEye className="h-5 w-5 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) =>
                                        setRememberMe(e.target.checked)
                                    }
                                    className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                                />
                                <label
                                    htmlFor="remember-me"
                                    className="ml-2 block text-base text-gray-900"
                                >
                                    Remember me
                                </label>
                            </div>

                            <div className="text-base">
                                <a
                                    href="/forgot-password"
                                    className="font-medium text-black hover:text-black hover:underline"
                                >
                                    Forgot Password?
                                </a>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                            >
                                {loading ? "Signing in..." : "Sign in"}
                            </button>
                        </div>
                    </form>

                    <p className="mt-6 text-center text-base text-gray-600">
                        Don't have an account?{" "}
                        <Link
                            to="/signup"
                            className="font-medium text-black hover:text-gray-700"
                        >
                            Sign Up
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
